import {ActionLink, Label} from "nhsuk-react-components"
import * as React from "react"
import {useContext, useEffect} from "react"
import {AppContext} from "../index"
import {axiosInstance} from "../requests/axiosInstance"
import {isInternalDev, isInternalDevSandbox, isSandbox} from "../services/environment"
import styled from "styled-components"

const Stacked = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding-top: 10px;
`

// Config is stored against the server session, which /login clears, so it must be set after login completes.
export const CONFIG_INITIALIZED_KEY = "eps-tool-config-initialized"

const HomePage: React.FC = () => {
  const {baseUrl, environment} = useContext(AppContext)

  useEffect(() => {
    if (sessionStorage.getItem(CONFIG_INITIALIZED_KEY)) {
      return
    }
    sessionStorage.setItem(CONFIG_INITIALIZED_KEY, "true")

    const setConfig = isInternalDev(environment) ? setDefaultDevConfig : setDefaultIntConfig
    setConfig(baseUrl).catch(() => sessionStorage.removeItem(CONFIG_INITIALIZED_KEY))
  }, [])

  return (
    <>
      <Label isPageHeading>I would like to...</Label>
      {isSandbox(environment)
        ? <ActionLink href={`${baseUrl}dose-to-text`}>Convert dose to text</ActionLink>
        : <>
          <Stacked>
            <ActionLink href={`${baseUrl}prescribe/load`}>Create Prescription(s)</ActionLink>
            <ActionLink href={`${baseUrl}dispense/release`}>Release Prescription(s)</ActionLink>
            <ActionLink href={`${baseUrl}search`}>Check Prescription(s) status</ActionLink>
            <ActionLink href={`${baseUrl}validate`}>Validate a FHIR Resource</ActionLink>
            <ActionLink href={`${baseUrl}compare-prescriptions`}>Compare Prescriptions</ActionLink>
            {isInternalDevSandbox(environment) && <ActionLink href={`${baseUrl}dose-to-text`}>Convert dose to text</ActionLink>}
          </Stacked>
        </>
      }
    </>
  )
}

interface Status {
  checks: {
    eps: Array<{
      version: string
    }>
  }
}

async function setDefaultDevConfig(
  baseUrl: string
): Promise<void> {
  const statusResult = await (await axiosInstance.get<Status>(`${baseUrl}_healthcheck`)).data
  const epsPrNumber = statusResult?.checks?.eps?.[0]?.version.match(/-pr-(\d+)/)?.[1] ?? ""
  const defaultDevConfig = {useSigningMock: true, epsPrNumber, signingPrNumber: "", useProxygen: true, forceFameBackend: false}
  await axiosInstance.post(`${baseUrl}config`, defaultDevConfig)
}

async function setDefaultIntConfig(
  baseUrl: string
): Promise<void> {
  const defaultIntConfig = {useProxygen: true}
  await axiosInstance.post(`${baseUrl}config`, defaultIntConfig)
}

export default HomePage
